import { worldRoot } from './worldRoot'
/**
 * Kenney set-dressing utilities — spawn GLB props using the PC-4a asset loader.
 * Props are placed once at scene init; placements come from the LAYOUT-1
 * floor plan (world/layout.ts). Décor items get deterministic seeded jitter.
 */
import * as pc from 'playcanvas'
import { getCachedAsset, onAssetReady } from '../assets'
import type { AssetZoneController } from '../assetZones'
import {
  SERVER_FURNITURE, DECOR_ITEMS, CHAIR_SEAT_LOCAL, SEAT_LOCAL_YAW,
  DESK_SURFACE_Y, decorJitter, deskClutterForSlot, type PropPlacement,
} from './layout'

/** Resolve a behavior-owned prop's stable Phase-4 parent. */
export function hqZoneRoot(app: pc.Application, zoneId: string): pc.Entity {
  const root = worldRoot(app)
  const zone = root.findByName?.(`hq-zone-${zoneId}`)
  return zone instanceof pc.Entity ? zone : root
}

// Palette (linear approximate of hex values)
export const PALETTE = {
  wood:       new pc.Color(0.690, 0.537, 0.408), // #b08968
  cream:      new pc.Color(0.961, 0.922, 0.875), // #f5ebe0
  sage:       new pc.Color(0.639, 0.694, 0.541), // #a3b18a
  terracotta: new pc.Color(0.878, 0.478, 0.373), // #e07a5f
  charcoal:   new pc.Color(0.239, 0.251, 0.357), // #3d405b
} as const

/** Ergonomic scale normalization for the half-scale office-kit desk set. */
export const STATION_DESK_SCALE = 1.9
export const STATION_CHAIR_SCALE = 1.75
export const STATION_LAPTOP_SCALE = 1.25
export const STATION_PLANT_SCALE = 2
/** The scaled desk top is 0.730 m; 0.75 keeps mounted props visibly above it. */
export const STATION_DESK_SURFACE_Y = DESK_SURFACE_Y

/**
 * Spawn a GLB prop, attaching to `parent` at `localPosition` (or to
 * `app.root` at world `position` when no parent is given).
 *
 * Returns the root entity immediately; render children arrive asynchronously.
 */
const STATIC_PROP_BATCH_MAX_AABB = 128
const staticPropBatchGroups = new WeakMap<pc.Application, number>()
type CanonicalStaticMaterial = {
  material: pc.StandardMaterial
  references: number
}
const canonicalStaticMaterials =
  new WeakMap<pc.Application, Map<string, CanonicalStaticMaterial>>()

function colorKey(color: pc.Color | null | undefined): string {
  if (!color) return ''
  return `${color.r.toFixed(4)},${color.g.toFixed(4)},${color.b.toFixed(4)}`
}

/**
 * Vendored KayKit furniture embeds the exact same atlas in every GLB, while
 * the untextured office kit repeats named palette materials across files.
 * Re-pointing those duplicates to one material is visually lossless and lets
 * PlayCanvas batch chairs, tables, shelves, and plants across asset files.
 */
function canonicalMaterialKey(
  file: string,
  material: pc.StandardMaterial,
): string | null {
  if (file.startsWith('kf_') && material.name === 'furniture_texture') {
    return 'kaykit:furniture_texture'
  }
  if (
    file.startsWith('synty_office_')
    && material.name === 'SyntyOffice_Atlas'
  ) {
    return 'synty-office:shared-atlas'
  }
  if (
    file.startsWith('prop_')
    && !material.diffuseMap
    && material.name.length > 0
  ) {
    return [
      'office-kit',
      material.name,
      colorKey(material.diffuse),
      colorKey(material.emissive),
      material.metalness.toFixed(4),
      material.gloss.toFixed(4),
      material.opacity.toFixed(4),
      String(material.blendType),
    ].join(':')
  }
  return null
}

function canonicalizeStaticPropMaterials(
  app: pc.Application,
  file: string,
  entity: pc.Entity,
): () => void {
  let registry = canonicalStaticMaterials.get(app)
  if (!registry) {
    registry = new Map()
    canonicalStaticMaterials.set(app, registry)
  }
  const retainedKeys: string[] = []
  for (const render of entity.findComponents('render') as pc.RenderComponent[]) {
    for (const mesh of render.meshInstances) {
      if (!(mesh.material instanceof pc.StandardMaterial)) continue
      if (
        file.startsWith('synty_office_') &&
        mesh.material.name === 'SyntyOffice_Glass' &&
        mesh.material.refraction !== 0
      ) {
        mesh.material.refraction = 0
        mesh.material.update()
      }
      const key = canonicalMaterialKey(file, mesh.material)
      if (!key) continue
      const canonical = registry.get(key)
      if (canonical) {
        mesh.material = canonical.material
        canonical.references += 1
      } else {
        registry.set(key, { material: mesh.material, references: 1 })
      }
      retainedKeys.push(key)
    }
  }
  let released = false
  return () => {
    if (released) return
    released = true
    for (const key of retainedKeys) {
      const canonical = registry.get(key)
      if (!canonical) continue
      canonical.references -= 1
      if (canonical.references === 0) registry.delete(key)
    }
  }
}

/**
 * One shared policy for immutable GLB dressing across HQ and its satellite
 * interiors. Spatial splitting keeps culling local even though every legacy
 * furnishing path now participates in the same batch vocabulary.
 */
function staticPropBatchGroup(app: pc.Application): number | undefined {
  const existing = staticPropBatchGroups.get(app)
  if (existing !== undefined) return existing
  const group = app.batcher?.addGroup(
    'arena-static-props',
    false,
    STATIC_PROP_BATCH_MAX_AABB,
  )
  if (!group) return undefined
  staticPropBatchGroups.set(app, group.id)
  return group.id
}

function createPropRoot(
  app: pc.Application,
  file: string,
  position: [number, number, number],
  rotY: number,
  uniformScale: number,
  parent?: pc.Entity,
): pc.Entity {
  const root = new pc.Entity(`prop-${file}`)
  root.setEulerAngles(0, rotY, 0)
  root.setLocalScale(uniformScale, uniformScale, uniformScale)
  if (parent) root.setLocalPosition(position[0], position[1], position[2])
  else root.setPosition(position[0], position[1], position[2])
  ;(parent ?? worldRoot(app)).addChild(root)
  return root
}

function attachPropAsset(
  app: pc.Application,
  root: pc.Entity,
  file: string,
  asset: pc.Asset,
  options: { batchGroupId?: number; castShadows?: boolean },
): () => void {
  if (!asset.resource) throw new Error(`Asset ${file} has no container resource`)
  const container = asset.resource as pc.ContainerResource
  const renderEntity = container.instantiateRenderEntity({
    castShadows: options.castShadows ?? false,
  })
  const releaseCanonicalMaterials = canonicalizeStaticPropMaterials(
    app,
    file,
    renderEntity,
  )
  if (options.batchGroupId !== undefined) {
    const renderComponents = renderEntity.findComponents('render') as unknown as Array<{
      batchGroupId: number
    }>
    for (const render of renderComponents) render.batchGroupId = options.batchGroupId
  }
  root.addChild(renderEntity)
  return () => {
    renderEntity.destroy()
    releaseCanonicalMaterials()
  }
}

export interface ZonedPropHandle {
  entity: pc.Entity
  setActive(active: boolean): void
  destroy(): void
}

/**
 * A room-owned zone prop. Registration exists only while its room is active,
 * so inactive coordinate islands retain placement/collision data without
 * retaining licensed render hierarchies.
 */
export function createZonedProp(
  app: pc.Application,
  assetZones: AssetZoneController<pc.Asset>,
  assetZone: string,
  file: string,
  position: [number, number, number],
  rotY = 0,
  uniformScale = 1,
  parent?: pc.Entity,
  options: { batchGroupId?: number; castShadows?: boolean } = {},
): ZonedPropHandle {
  const root = createPropRoot(app, file, position, rotY, uniformScale, parent)
  const attachOptions = {
    ...options,
    batchGroupId: options.batchGroupId ?? staticPropBatchGroup(app),
  }
  let unregister: (() => void) | undefined
  let destroyed = false
  return {
    entity: root,
    setActive(active) {
      if (destroyed || active === Boolean(unregister)) return
      if (active) {
        unregister = assetZones.register(
          assetZone,
          file,
          (asset) => attachPropAsset(app, root, file, asset, attachOptions),
        )
        return
      }
      unregister?.()
      unregister = undefined
    },
    destroy() {
      if (destroyed) return
      destroyed = true
      unregister?.()
      unregister = undefined
      root.destroy()
    },
  }
}

export function spawnProp(
  app: pc.Application,
  file: string,
  position: [number, number, number],
  rotY = 0,
  uniformScale = 1,
  parent?: pc.Entity,
  options: {
    batchGroupId?: number
    castShadows?: boolean
    assetZones?: AssetZoneController<pc.Asset>
    assetZone?: string
  } = {},
): pc.Entity {
  const root = createPropRoot(
    app,
    file,
    position,
    rotY,
    uniformScale,
    parent,
  )

  const batchGroupId =
    options.batchGroupId ?? staticPropBatchGroup(app)

  function attach(asset: pc.Asset): () => void {
    return attachPropAsset(app, root, file, asset, {
      batchGroupId,
      castShadows: options.castShadows,
    })
  }

  if (options.assetZones && options.assetZone) {
    options.assetZones.register(options.assetZone, file, attach)
  } else {
    const cached = getCachedAsset(file)
    if (cached) {
      attach(cached)
    } else {
      onAssetReady(file, attach)
    }
  }

  return root
}

/** Spawn multiple scene-level props into app.root. */
export function spawnProps(
  app: pc.Application,
  defs: Array<{
    file: string
    position: [number, number, number]
    rotY?: number
    scale?: number
  }>,
): pc.Entity[] {
  return defs.map(({ file, position, rotY = 0, scale = 1 }) =>
    spawnProp(app, file, position, rotY, scale),
  )
}

// ── Procedural helpers ────────────────────────────────────────────────────

let _matCache: Map<string, pc.StandardMaterial> | null = null

function proceduralMat(key: 'wood' | 'terracotta' | 'charcoal'): pc.StandardMaterial {
  if (!_matCache) _matCache = new Map()
  const cached = _matCache.get(key)
  if (cached) return cached
  const mat = new pc.StandardMaterial()
  mat.diffuse = PALETTE[key]
  mat.gloss = key === 'terracotta' ? 0.05 : 0.4
  mat.update()
  _matCache.set(key, mat)
  return mat
}

function proceduralBox(
  app: pc.Application,
  name: string,
  pos: [number, number, number],
  scale: [number, number, number],
  mat: pc.Material,
  parent?: pc.Entity,
): pc.Entity {
  const e = new pc.Entity(name)
  e.addComponent('render', { type: 'box' })
  if (parent) e.setLocalPosition(pos[0], pos[1], pos[2])
  else e.setPosition(pos[0], pos[1], pos[2])
  e.setLocalScale(scale[0], scale[1], scale[2])
  if (e.render) {
    for (const mi of e.render.meshInstances) mi.material = mat
  }
  const target = parent ?? worldRoot(app)
  target.addChild(e)
  return e
}

/** Build the procedural (file: null) placements — tables, rug, cabinet. */
function buildProcedural(
  app: pc.Application,
  p: PropPlacement,
  parent?: pc.Entity,
): pc.Entity[] {
  switch (p.id) {
    case 'kitchen-rug':
      // Flat terracotta box, resting on the floor.
      return [proceduralBox(app, p.id, [p.x, 0.01, p.z], [p.halfW * 2, 0.02, p.halfD * 2], proceduralMat('terracotta'), parent)]
    case 'gallery-rug':
      // Flat charcoal runner under the gallery wall.
      return [proceduralBox(app, p.id, [p.x, 0.01, p.z], [p.halfW * 2, 0.02, p.halfD * 2], proceduralMat('charcoal'), parent)]
    case 'kitchen-table':
      // Low coffee table.
      return [proceduralBox(app, p.id, [p.x, 0.2, p.z], [p.halfW * 2, 0.4, p.halfD * 2], proceduralMat('charcoal'), parent)]
    case 'meeting-table':
      // Wood table top on two charcoal leg slabs.
      return [
        proceduralBox(app, `${p.id}-top`, [p.x, 0.72, p.z], [p.halfW * 2, 0.08, p.halfD * 2], proceduralMat('wood'), parent),
        proceduralBox(app, `${p.id}-leg-w`, [p.x - p.halfW + 0.15, 0.34, p.z], [0.08, 0.68, p.halfD * 2 - 0.3], proceduralMat('charcoal'), parent),
        proceduralBox(app, `${p.id}-leg-e`, [p.x + p.halfW - 0.15, 0.34, p.z], [0.08, 0.68, p.halfD * 2 - 0.3], proceduralMat('charcoal'), parent),
      ]
    case 'server-cabinet':
      return [proceduralBox(app, p.id, [p.x, 0.6, p.z], [p.halfW * 2, 1.2, p.halfD * 2], proceduralMat('charcoal'), parent)]
    default:
      return []
  }
}

/**
 * Spawn a floor-plan placement: GLB props via the manifest loader, procedural
 * geometry otherwise. Décor items get deterministic seeded jitter (±5 cm,
 * ±10°, keyed by placement id); everything else is placed exactly.
 */
export function spawnPlacement(
  app: pc.Application,
  p: PropPlacement,
  parent?: pc.Entity,
): pc.Entity[] {
  let { x, z, rotY } = p
  if (p.decor) {
    const j = decorJitter(p.id)
    x += j.dx
    z += j.dz
    rotY += j.drotY
  }
  if (p.file === null) return buildProcedural(app, p, parent)
  return [spawnProp(app, p.file, [x, p.y, z], rotY, p.scale ?? 1, parent)]
}

function spawnPlacements(
  app: pc.Application,
  placements: readonly PropPlacement[],
  parent?: pc.Entity,
): pc.Entity[] {
  const out: pc.Entity[] = []
  for (const p of placements) out.push(...spawnPlacement(app, p, parent))
  return out
}

// ── Per-station GLB props ─────────────────────────────────────────────────

/**
 * Spawn Kenney props for a single desk station as children of `stationRoot`.
 * Positions are in local station space: the occupant faces local -Z (the
 * cluster spine). Desk + tucked office chair + monitor + keyboard + plant.
 * Deliberately exact — desks share cluster alignment, no jitter.
 */
export function spawnStationProps(
  app: pc.Application,
  stationRoot: pc.Entity,
  options: { batchGroupId?: number } = {},
): pc.Entity[] {
  const renderOptions = {
    batchGroupId: options.batchGroupId,
    // Repeating desk pods are not hero furniture. Keeping them out of the
    // directional cascades avoids multiplying every station mesh by 3–4.
    castShadows: false,
  }
  return [
    // Desk (centred, negative z is "back"/spine side)
    // The source origin sits at one corner. Offset the 1.9x model so its
    // measured 1.40 x 0.75 m footprint is centered on the station.
    spawnProp(app, 'prop_desk.glb',
      [-0.68, 0, -0.05], 0, STATION_DESK_SCALE, stationRoot, renderOptions),
    // Office chair, tucked under the desk edge, facing the monitor.
    // Placement comes from layout.ts so the seated character lands on it.
    spawnProp(app, 'prop_chairDesk.glb',
      [CHAIR_SEAT_LOCAL.dx + 0.29, 0, CHAIR_SEAT_LOCAL.dz - 0.27],
      SEAT_LOCAL_YAW, STATION_CHAIR_SCALE, stationRoot, renderOptions),
    // A laptop on the desk instead of the old oversized monitor slab, which
    // towered over the desk and read as a black wall rather than a screen.
    spawnProp(app, 'prop_laptop.glb',
      [-0.17, STATION_DESK_SURFACE_Y, -0.27],
      0, STATION_LAPTOP_SCALE, stationRoot, renderOptions),
    // Plant at the desk corner
    spawnProp(app, 'prop_plantSmall1.glb',
      [0.62, 0, -0.62], 0, STATION_PLANT_SCALE, stationRoot, renderOptions),
  ]
}

/**
 * LAYOUT-2 clutter pass: deterministic per desk slot. Spawns 0-3 Kenney
 * props (papers / desk phone / box under the desk) as children of the
 * station root. Bare slots get nothing — see deskClutterForSlot.
 */
export function spawnDeskClutter(
  app: pc.Application,
  stationRoot: pc.Entity,
  slot: number,
  batchGroupId?: number,
): pc.Entity[] {
  return deskClutterForSlot(slot).map((item) =>
    spawnProp(
      app,
      item.file,
      [item.dx, item.dy, item.dz],
      item.rotY,
      item.scale,
      stationRoot,
      { batchGroupId, castShadows: false },
    ),
  )
}

// ── Server room dressing (NW room) ────────────────────────────────────────

export interface ServerRoomDressingHandles {
  entities: pc.Entity[]
  indicatorMats: pc.StandardMaterial[]
}

export function setupServerRoomDressing(app: pc.Application): ServerRoomDressingHandles {
  const entities: pc.Entity[] = []
  const indicatorMats: pc.StandardMaterial[] = []
  const parent = hqZoneRoot(app, 'l0-server-room')

  // Behavior-owned racks and controls keep their lifecycle here, but share
  // the authoritative Phase-4 server-room parent and local coordinate space.
  entities.push(...spawnPlacements(app, SERVER_FURNITURE, parent))

  // One physical status plate per real server rack: the two baseline racks
  // plus the four Phase-5 inventory racks. Identity remains Portal-owned;
  // these deliberately carry only a stable physical slot index.
  const racks = [
    { x: -21.6, y: 1.05, z: -13.72, rotY: 0, scale: 1 },
    { x: -20.2, y: 1.05, z: -13.72, rotY: 0, scale: 1 },
    { x: -23.48, y: 0.95, z: -12.8, rotY: 90, scale: 0.9 },
    { x: -23.48, y: 0.95, z: -11.2, rotY: 90, scale: 0.9 },
    { x: -18.52, y: 0.95, z: -13.2, rotY: 270, scale: 0.9 },
    { x: -18.52, y: 0.95, z: -11.8, rotY: 270, scale: 0.9 },
  ] as const
  for (let i = 0; i < racks.length; i++) {
    const rack = racks[i]
    const mat = new pc.StandardMaterial()
    mat.diffuse = new pc.Color(0.02, 0.02, 0.02)
    mat.emissive = new pc.Color(0.18, 0.36, 0.54)
    mat.emissiveIntensity = 1.0
    mat.update()
    indicatorMats.push(mat)

    const box = new pc.Entity(`serverIndicator-${i}`)
    box.addComponent('render', { type: 'box' })
    box.setLocalScale(0.34 * rack.scale, 0.08, 0.04)
    box.setLocalPosition(rack.x, rack.y, rack.z)
    box.setLocalEulerAngles(0, rack.rotY, 0)
    if (box.render) {
      for (const mi of box.render.meshInstances) mi.material = mat
    }
    parent.addChild(box)
    entities.push(box)
  }

  return { entities, indicatorMats }
}

// The generic L0 meeting/reception/bay kits were pre-Phase-4 placeholders.
// Authoritative furnishings now live in HQ_FURNITURE under stable zone roots;
// only unprogrammed office-edge decor remains at scene ownership level.
export function setupOfficeDecor(app: pc.Application): pc.Entity[] {
  return spawnPlacements(app, DECOR_ITEMS)
}
